import React, { useMemo, useRef } from 'react';
import { Position, BaseEdge, EdgeLabelRenderer } from '@xyflow/react';
import { LuBot } from 'react-icons/lu';
import dagre from 'dagre';
import BhHandle from 'blob_types/blobhub/compute/workflow/flowkit/handle';
import { BhFlowNode, BhFlowTitle, BhFlowSubtitle, BhFlowTags, BhFlowTag }
  from 'blob_types/blobhub/compute/workflow/flowkit/flowNode';
import { edgeStrokeStyle } from 'blob_types/blobhub/compute/workflow/flowkit/flowEdge';
import { reconcileElements } from 'blob_types/blobhub/compute/workflow/flowkit/reconcile';
import { agentLabel } from '../../domain/cellWorkflow';
import WidgetStatus from '../../../common/widgetStatus';
import GraphCanvas from '../../components/graphCanvas';

// Measured, not guessed — but per SHAPE, not as one constant: the rendered boxes are genuinely
// different heights (a sentinel is a single title line; a worker_task step with a chip is three
// rows), and feeding dagre one height for all of them centred every edge on a common midline that
// did not match any node's real visual centre. Each was DUMPED from the real component tree
// (WorkflowStepNode rendered for real, its `.bh-flow-node` outerHTML captured) into a static page
// that links the bundle's real CSS, in bundle order (bootstrap.css, @xyflow/react/dist/style.css,
// flowkit.css, missionControl.css, cells.css), then read back via getBoundingClientRect().height in
// headless Chrome:
//   sentinel (@start/@end, title only)                                    36.625  -> 37
//   worker_task WITH a chip (pickable-with-agent and read-only-with-agent   76.21875 -> 77
//     measure byte-identical — the button's `display: contents`, see cells.css)
//   worker_task PICKABLE with no agent yet ("set agent…" invitation)       74.71875 -> 75
//   a step with no chip at all (external_input: title + type, no tag row)  51.21875 -> 52
// Each value is Math.ceil of the measured height so the node never clips its own content.
const NODE_H = { sentinel: 37, workerTaskChip: 77, workerTaskInvitation: 75, plain: 52 };

// Which of the measured shapes above a given node def renders as — mirrors WorkflowStepNode's own
// `hasAgentChip = data.agent || data.onSelectAgent` gate exactly, so the height fed to dagre can
// never disagree with what actually gets drawn. `pickable` is `!!(onSelectStep && n.canPickAgent)` —
// already computed once per node by the caller.
function nodeHeight(n, pickable) {
  if (n.sentinel) return NODE_H.sentinel;
  if (!n.agent && !pickable) return NODE_H.plain;
  return n.agent ? NODE_H.workerTaskChip : NODE_H.workerTaskInvitation;
}
// Reserved routing endpoints. They carry no id in `nodes`, but we DO render them as (distinct) sentinel
// nodes so @start → … → @end reads as a complete flow.
const START = '@start';
const END = '@end';

// Auto width: size each node to its label so long step ids aren't clipped and short ones aren't padded.
// A cheap character estimate (no DOM measuring) is enough to keep the dagre layout overlap-free, because
// the rendered node is pinned to this same width.
function nodeWidth(...texts) {
  const len = Math.max(0, ...texts.map(t => String(t || '').length));
  return Math.round(Math.min(280, Math.max(72, len * 8 + 34)));
}

// What a pickable step with no agent yet says on its chip: an invitation, not a value — wording that
// cannot be mistaken for a real agent name the way a blank or a dash could.
const AGENT_UNSET_LABEL = 'set agent…';

// Flow-kit node for one workflow step / sentinel: the id only, with a left TARGET handle (incoming)
// and a right SOURCE handle (outgoing) so left-to-right edges anchor to it. Static everywhere except
// the agent chip, which becomes a real button — tap-to-pick — on a surface that wired onSelectStep.
//
// The chip is the same in-node tag the mission graph's own cards render (BhFlowTags/BhFlowTag), with
// a leading LuBot glyph — the same icon set and size (11px) missionGraph.js uses for ITS own tags
// (LuWorkflow, LuFolder), and the same icon AgentPopover leads each of its rows with, so the chip
// reads as one of that family rather than a special case.
//
// It renders whenever there is an agent to NAME or a way to SET one — `data.agent ||
// data.onSelectAgent` — not on `data.agent` alone. Gating on the agent alone meant a step with no
// agent rendered nothing even when it was pickable (`onSelectAgent` present), so an agent could be
// CHANGED but never SET: exactly the population an API-built or raw-JSON step belongs to. A step that
// is neither pickable nor carries an agent still renders nothing.
export function WorkflowStepNode({ id, data, selected }) {
  const isSentinel = !!(data && data.sentinel);
  const attachments = (
    <>
      <BhHandle mode="fixed" type="target" position={Position.Left} id="in" isConnectable={false} />
      <BhHandle mode="fixed" type="source" position={Position.Right} id="out" isConnectable={false} />
    </>
  );
  const hasAgentChip = !!(data && (data.agent || data.onSelectAgent));
  // The glyph only stands for a REAL agent — the "set agent…" invitation names none to show.
  const agentTag = hasAgentChip && (
    <BhFlowTag icon={data.agent ? <LuBot size={11} /> : null}>
      {data.agent || AGENT_UNSET_LABEL}
    </BhFlowTag>
  );
  return (
    <BhFlowNode
      selected={selected}
      className={`mc-workflow-step-node${isSentinel ? ' mc-workflow-sentinel-node' : ''}`}
      attachments={attachments}
    >
      <BhFlowTitle>{(data && data.label) || id}</BhFlowTitle>
      {data && data.type && <BhFlowSubtitle>{data.type}</BhFlowSubtitle>}
      {hasAgentChip && (
        <BhFlowTags>
          {data.onSelectAgent ? (
            <button type="button" className="mc-workflow-agent nodrag nopan" data-step-id={id}
              onClick={data.onSelectAgent}>
              {agentTag}
            </button>
          ) : agentTag}
        </BhFlowTags>
      )}
    </BhFlowNode>
  );
}

const NODE_TYPES = { workflowStep: WorkflowStepNode };

// Own elongated arrowhead. ReactFlow's built-in ArrowClosed is squat AND its square viewBox preserves
// aspect ratio, so widening the marker alone never lengthens it — hence a real 2:1 marker here (same
// shape as the mission edge's, just longer).
const WF_ARROW_ID = 'mc-wf-edge-arrow';

function WorkflowEdgeMarkers() {
  return (
    <svg style={{ position: 'absolute', width: 0, height: 0 }} aria-hidden="true">
      <defs>
        <marker
          id={WF_ARROW_ID}
          markerWidth="16"
          markerHeight="8"
          viewBox="0 0 16 8"
          orient="auto"
          refX="16"
          refY="4"
          markerUnits="userSpaceOnUse"
        >
          {/* `style`, not the `fill` presentation attribute: var() in a presentation attribute is
              unsupported on ios_saf 11, still a production target, where it would paint the
              arrowhead black. Same reasoning as flowkit/flowEdge.js. */}
          <path d="M0,0 L16,4 L0,8 Z" style={{ fill: 'var(--bh-edge)' }} />
        </marker>
      </defs>
    </svg>
  );
}

// Lateral separation (px) between routed polylines that share the same pair of endpoints. A cell
// workflow can be cyclic (the shipped `agent_with_review`: work → ask and ask → work both exist), and
// two conditional edges can also run the same (from, to) with different `when` guards — dagre treats
// every setEdge(from, to) call as ONE graph edge, so without this, either case hands back byte-IDENTICAL
// `points` for both edges and they draw on top of each other.
const EDGE_PARALLEL_GAP = 20;

// How much of the full parallel offset survives AT each endpoint. Zero — tapering the bow all the way
// out — put both directions of a pair on the identical point at each node, so the two arrows met and
// read as one line splitting rather than two arrows with a gap. The full offset at the endpoint read
// as poorly connected instead. Keeping half separates them right up to the node while they still
// visibly gravitate toward its centre. The preview renders zoomed OUT (fitView caps at 0.8 and can
// land near 0.3 on a wide flow), so these are world units that shrink on screen — which is why the
// gap is set here rather than left at the few px that looked adequate at zoom 1.
const EDGE_END_OFFSET_FRACTION = 0.5;

// Unordered pair key — A→B and B→A (and any repeat of either) group together, since it is the PAIR
// that must not overlap, not the direction.
function edgePairKey(from, to) {
  return from < to ? `${from}|${to}` : `${to}|${from}`;
}

// Perpendicular offset for one edge among `count` sharing a pair, at its own `index` within that
// group. A lone edge (count <= 1) gets 0 and stays dead centre on dagre's own route — only once there
// is something to fan apart from do parallel edges bow out, symmetrically around the centre line.
// Mirrors the property-graph explorer's own parallel-edge treatment (playground/widgets/graph/edges.js
// — parallelOffset), which is exactly this "gravitate to the middle unless there's more than one" rule.
function parallelOffset(index, count) {
  if (!count || count <= 1) return 0;
  return (index - (count - 1) / 2) * EDGE_PARALLEL_GAP;
}

// Bows a routed polyline sideways by `offset`, perpendicular to the straight line between the pair's
// two node CENTRES (not each edge's own direction — using a canonical direction keyed off the
// endpoints' sorted ids is what keeps A→B and B→A bowing to OPPOSITE sides rather than cancelling
// each other out). The offset EASES from a fraction of itself at each endpoint (EDGE_END_OFFSET_FRACTION)
// to the full amount at the polyline's midpoint (by arc length, via a sine), so a paired edge (a
// cycle's two directions) bows apart in the space BETWEEN its nodes AND stays visibly separated where
// it meets each node — a lone edge (offset 0) is untouched and sits dead centre.
//
// The first/last point is snapped to its own node's centre y BEFORE the offset is applied: dagre
// routes a cycle's back edge with a small bow of its own right at the node (measured — a reversed
// edge's raw endpoint can sit a few px off centre on its own), and that residual is not symmetric
// between the two directions. Snapping first discards it, so what remains at the endpoint is only our
// own deliberate, symmetric offset rather than ours plus dagre's.
function offsetPoints(points, offset, aCenter, bCenter, sourceCenterY, targetCenterY) {
  if (!points || points.length === 0) return points;
  const last = points.length - 1;
  let pts = points.map((p, i) => {
    if (i === 0) return { x: p.x, y: sourceCenterY };
    if (i === last) return { x: p.x, y: targetCenterY };
    return p;
  });
  if (!offset) return pts;
  // A route with no interior point (just the two endpoints) has nothing to bow around — give it one
  // at the midpoint so a paired edge still visibly separates instead of overlapping straight.
  if (pts.length === 2) {
    pts = [pts[0], { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 }, pts[1]];
  }
  const newLast = pts.length - 1;
  const dx = bCenter.x - aCenter.x;
  const dy = bCenter.y - aCenter.y;
  const len = Math.hypot(dx, dy) || 1;
  const ox = -dy / len;
  const oy = dx / len;
  const arc = [0];
  for (let i = 1; i < pts.length; i++) {
    arc.push(arc[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
  }
  const total = arc[newLast] || 1;
  return pts.map((p, i) => {
    const t = arc[i] / total;
    const shape = EDGE_END_OFFSET_FRACTION + (1 - EDGE_END_OFFSET_FRACTION) * Math.sin(Math.PI * t);
    const ease = shape * offset;
    return { x: p.x + ox * ease, y: p.y + oy * ease };
  });
}

// A rounded polyline through dagre's routed points, so the edge follows the layout AROUND the nodes
// instead of a straight bezier that cuts through them. Corners are softened with short quadratic arcs.
function pointsToPath(pts, radius = 8) {
  if (!pts || pts.length < 2) return '';
  if (pts.length === 2) return `M ${pts[0].x},${pts[0].y} L ${pts[1].x},${pts[1].y}`;
  let d = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const next = pts[i + 1];
    const clamp = (from) => {
      const dx = curr.x - from.x;
      const dy = curr.y - from.y;
      const len = Math.hypot(dx, dy) || 1;
      const r = Math.min(radius, len / 2);
      return { x: curr.x - (dx / len) * r, y: curr.y - (dy / len) * r };
    };
    const a = clamp(prev);
    const dx2 = next.x - curr.x;
    const dy2 = next.y - curr.y;
    const len2 = Math.hypot(dx2, dy2) || 1;
    const r2 = Math.min(radius, len2 / 2);
    const b = { x: curr.x + (dx2 / len2) * r2, y: curr.y + (dy2 / len2) * r2 };
    d += ` L ${a.x},${a.y} Q ${curr.x},${curr.y} ${b.x},${b.y}`;
  }
  const last = pts[pts.length - 1];
  d += ` L ${last.x},${last.y}`;
  return d;
}

// Edge that draws dagre's routed polyline (data.points) with the arrowhead + optional `when` label.
function WorkflowEdge({ id, data, style }) {
  const pts = (data && data.points) || [];
  const path = pointsToPath(pts);
  if (!path) return null;
  const mid = pts[Math.floor((pts.length - 1) / 2)] || pts[0];
  const label = data && data.label;
  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={`url(#${WF_ARROW_ID})`} style={style} />
      {label && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${mid.x}px, ${mid.y}px)`,
              fontSize: 11,
              color: 'var(--bh-text-muted-5)',
              // The chip's plate must MATCH the ground it floats over, not contrast with it: the plate
              // exists to knock the edge stroke out from behind the label, and any visible difference
              // would read as a box nobody drew. `.mc-workflow-graph` takes --bh-surface (it used to
              // hold a sunken tint of its own, until it was aligned with every other graph preview),
              // so this is the same token by construction — the mission map's own edge labels have
              // always worked this way on the card. Naming the canvas token would put a visible plate
              // back. planeProbe.js measures the pair and expects 1.000.
              background: 'var(--bh-surface)',
              padding: '0 3px',
              borderRadius: 3,
              pointerEvents: 'none',
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const EDGE_TYPES = { workflowEdge: WorkflowEdge };

/**
 * What the drawing depends on, as one comparable string.
 *
 * The workflow arrives as the cell's EMBEDDED copy, and the store rebuilds itself from the network on
 * every session event — so an untouched cell hands this a fresh-but-equal object several times a
 * minute. Keyed on that object's IDENTITY, the whole element set was rebuilt each time; ReactFlow then
 * re-measures every node it has never seen before, and the frame before it has measured them is the
 * flicker. Keyed on the content, an unchanged workflow re-renders the same objects and nothing moves.
 *
 * Everything the builder reads goes in — step ids and types, each edge's endpoints and `when`, the
 * agent running each step, and the highlighted step, which is a node's `selected`.
 */
export function workflowSignature(workflow, currentStep) {
  const nodes = (workflow && Array.isArray(workflow.nodes)) ? workflow.nodes : [];
  const edges = (workflow && Array.isArray(workflow.edges)) ? workflow.edges : [];
  return [
    nodes.map(n => `${n && n.id}:${(n && (n.ty || n.type)) || ''}:${agentLabel(n) || ''}`).join(','),
    edges.map(e => `${e && e.from}->${e && e.to}:${(e && e.when) || ''}`).join(','),
    currentStep || '',
  ].join('#');
}

/**
 * WorkflowGraph — read-only flow-kit rendering of a cell workflow. Real steps (labeled by their step id,
 * with the step type as a small muted second line) plus the @start / @end sentinels are laid out
 * left-to-right (dagre); each edge follows dagre's routed polyline AROUND intermediate nodes (not a
 * straight bezier through them) and is the workflow's from→to
 * routing with a direction arrowhead and its optional `when` label. Nodes auto-size to their text and the
 * layout is fed those widths so nothing overlaps. Uses the shared canvas (GraphCanvas) + flow-kit styling;
 * zoom is by mouse wheel (no on-canvas zoom controls).
 *
 * Props:
 *   workflow     — { nodes: [{ id, ... }], edges: [{ from, to, when? }] }
 *   currentStep  — optional step id to highlight (the cell's active step)
 *   onSelectStep — optional (stepId) => void. When present, every `worker_task` step's agent chip
 *                  becomes a real button that calls it with that step's id — the caller's own way of
 *                  tracking which step is picked. That includes a `worker_task` step with NO agent
 *                  yet: the button still renders, inviting one to be set, rather than disappearing —
 *                  an agent can be set this way, not just changed. Omitted, the chip (where there is
 *                  one) is inert text: this is what keeps the read-only surfaces read-only.
 *                  Deliberately NOT ReactFlow node selection — `selected` still means "this is where
 *                  the run is", and a preview whose run marker anybody could drag would stop
 *                  reporting anything.
 */
export default function WorkflowGraph({ workflow, currentStep, onSelectStep }) {
  // The affordance turning on/off is a content change as far as the memo below is concerned — a
  // callback appearing out of nowhere would otherwise redraw nothing, since workflowSignature only
  // covers what the WORKFLOW carries.
  const signature = `${workflowSignature(workflow, currentStep)}#${onSelectStep ? '1' : ''}`;
  // What is on screen, so a real change can be merged onto it instead of replacing it — the step mark
  // moving is news about two nodes, and the rest keep what ReactFlow measured (flowkit/reconcile.js).
  const shown = useRef({ rfNodes: [], rfEdges: [] });
  const { rfNodes, rfEdges, hasContent } = useMemo(() => {
    const steps = (workflow && Array.isArray(workflow.nodes))
      ? workflow.nodes.filter(n => n && n.id && n.id !== START && n.id !== END)
      : [];
    const wfEdges = (workflow && Array.isArray(workflow.edges)) ? workflow.edges.filter(Boolean) : [];

    // Node set = real steps + whichever sentinels the edges actually reference.
    const referenced = new Set();
    wfEdges.forEach(e => { referenced.add(e.from); referenced.add(e.to); });
    const nodeDefs = steps.map(n => ({
      id: n.id, label: n.id, sentinel: false, type: n.ty || n.type || null, agent: agentLabel(n),
      // Only a worker_task step ever runs an agent — the one kind of step the chip can ever pick for.
      canPickAgent: (n.ty || n.type) === 'worker_task',
    }));
    if (referenced.has(START)) nodeDefs.push({ id: START, label: START, sentinel: true });
    if (referenced.has(END)) nodeDefs.push({ id: END, label: END, sentinel: true });

    const nodeIds = new Set(nodeDefs.map(n => n.id));
    // One edge per (from,to,when) tuple, both endpoints present.
    const seen = new Set();
    const edgeDefs = wfEdges.filter(e => {
      if (!nodeIds.has(e.from) || !nodeIds.has(e.to)) return false;
      const k = `${e.from}->${e.to}:${e.when || ''}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    // dagre LR layout, fed each node's real (auto) width so ranks/rows never overlap.
    const g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: 'LR', nodesep: 50, ranksep: 96, marginx: 18, marginy: 18 });
    g.setDefaultEdgeLabel(() => ({}));
    const widthOf = {};
    const heightOf = {};
    for (const n of nodeDefs) {
      // The same fallback the node itself renders (WorkflowStepNode) when it is pickable but carries
      // no agent yet — sized for it here too, or the invitation text would overflow a node that was
      // never told to make room for it.
      const pickable = !!(onSelectStep && n.canPickAgent);
      const agentText = n.agent || (pickable ? AGENT_UNSET_LABEL : null);
      const w = nodeWidth(n.label, n.type, agentText);
      const h = nodeHeight(n, pickable);
      widthOf[n.id] = w;
      heightOf[n.id] = h;
      g.setNode(n.id, { width: w, height: h });
    }
    for (const e of edgeDefs) g.setEdge(e.from, e.to);
    dagre.layout(g);

    const rfNodes = nodeDefs.map(n => {
      const gn = g.node(n.id);
      const w = widthOf[n.id];
      const h = heightOf[n.id];
      return {
        id: n.id,
        type: 'workflowStep',
        position: gn ? { x: gn.x - w / 2, y: gn.y - h / 2 } : { x: 0, y: 0 },
        selected: n.id === currentStep,
        draggable: false,
        data: {
          label: n.label, sentinel: n.sentinel, type: n.type, agent: n.agent,
          onSelectAgent: (onSelectStep && n.canPickAgent) ? () => onSelectStep(n.id) : undefined,
        },
        style: { width: w },
      };
    });

    // Position among edges sharing the same PAIR of endpoints (direction and `when` both irrelevant —
    // see EDGE_PARALLEL_GAP's comment above), assigned in edgeDefs' own order.
    const pairCounts = {};
    edgeDefs.forEach(e => {
      const k = edgePairKey(e.from, e.to);
      pairCounts[k] = (pairCounts[k] || 0) + 1;
    });

    const pairSeen = {};

    const rfEdges = edgeDefs.map((e, i) => {
      const de = g.edge(e.from, e.to);
      const k = edgePairKey(e.from, e.to);
      const parallelIndex = pairSeen[k] || 0;
      pairSeen[k] = parallelIndex + 1;
      const offset = parallelOffset(parallelIndex, pairCounts[k]);
      // Canonical (sorted-id) endpoint order so every edge sharing this pair — whichever direction it
      // runs — offsets relative to the SAME reference line, landing on opposite sides rather than
      // cancelling out.
      const [aId, bId] = e.from < e.to ? [e.from, e.to] : [e.to, e.from];
      // A PAIRED edge is re-based onto the straight line between its two nodes, keeping only dagre's
      // endpoint x (the node boundary the arrow should touch) and dropping its routed interior.
      // dagre bends a cycle's forward and reversed edge differently — measured on the shipped
      // agent_with_review, the two bowed -18.9 and +8.8 from their own centre line — so a symmetric
      // offset laid over two different curves still drew a lopsided lens. Re-basing both on the same
      // straight line makes them differ ONLY by their offset: exact mirror images, straddling the
      // line between the nodes rather than floating to one side of it. offsetPoints inserts the
      // midpoint a 2-point route needs, so this stays a straight line until the bow is applied.
      // A LONE edge keeps dagre's route untouched — it has nothing to be symmetric with, and that
      // route is what steers it around whatever the layout put in the way.
      const dagrePoints = (de && de.points) || [];
      const paired = pairCounts[k] > 1 && dagrePoints.length > 0;
      const raw = paired
        ? [{ x: dagrePoints[0].x, y: g.node(e.from).y },
           { x: dagrePoints[dagrePoints.length - 1].x, y: g.node(e.to).y }]
        : dagrePoints;
      const points = offsetPoints(
        raw, offset, g.node(aId), g.node(bId), g.node(e.from).y, g.node(e.to).y,
      );
      return {
        id: `we-${i}-${e.from}-${e.to}`,
        source: e.from,
        target: e.to,
        sourceHandle: 'out',
        targetHandle: 'in',
        type: 'workflowEdge',
        style: edgeStrokeStyle(false),
        // dagre's routed points make the edge follow the layout around intermediate nodes; offsetPoints
        // fans genuinely-parallel edges apart (a lone edge is untouched — offset is 0).
        data: { points, label: e.when || undefined },
      };
    });

    shown.current = {
      rfNodes: reconcileElements(shown.current.rfNodes, rfNodes),
      rfEdges: reconcileElements(shown.current.rfEdges, rfEdges),
    };
    return { ...shown.current, hasContent: nodeDefs.length > 0 };
    // Keyed on the CONTENT, not on the objects carrying it — see workflowSignature above.
  }, [signature]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasContent) {
    return <WidgetStatus variant="light">No steps.</WidgetStatus>;
  }

  return (
    <div className="mc-workflow-graph">
      <WorkflowEdgeMarkers />
      <GraphCanvas
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        fitView
        selectable={false}
        attribution={false}
        showControls={false}
        fitViewOptions={{ maxZoom: 0.8, padding: 0.16 }}
        minZoom={0.2}
      />
    </div>
  );
}
